import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';

const logoUrl = `${import.meta.env.BASE_URL}logo.jpg`;

export function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation() as any;
    const redirectTo = location.state?.from?.pathname || '/';

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError('');
        setIsSubmitting(true);
        try {
            await login(username.trim(), password);
            navigate(redirectTo, { replace: true });
        } catch (err: any) {
            setError(err?.message || 'Login failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-950 text-slate-50">
            <div className="relative min-h-screen flex items-center justify-center px-6 py-16">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />
                    <div className="absolute top-1/3 -right-20 h-80 w-80 rounded-full bg-cyan-400/6 blur-3xl" />
                </div>

                <div className="relative w-full max-w-4xl grid gap-8 lg:grid-cols-2 items-center">
                    <div className="hidden lg:flex flex-col items-start gap-6 pr-6">
                        <div className="w-48">
                            <img src={logoUrl} alt="HADAF Composites" className="w-full h-auto object-contain rounded-md shadow-md" />
                        </div>
                        <h2 className="text-3xl font-extrabold text-white">HADAF Composites</h2>
                        <p className="text-slate-300 leading-relaxed max-w-md">
                            Durable, lightweight composite solutions engineered for marine and industrial applications. Access production, inventory, and project workflows securely.
                        </p>
                        <div className="flex flex-col gap-3 mt-2 text-sm text-slate-300">
                            <div className="flex items-center gap-3">
                                <div className="h-2 w-2 rounded-full bg-emerald-400" />
                                <span>Coastal-grade corrosion resistance</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="h-2 w-2 rounded-full bg-emerald-400" />
                                <span>Consistent, precision manufacturing</span>
                            </div>
                        </div>
                        <div className="mt-auto text-xs text-slate-400">Version {((import.meta as any).env?.VITE_APP_VERSION || (import.meta as any).env?.REACT_APP_VERSION || '1.0.0')}</div>
                    </div>

                    <div className="flex justify-center">
                        <Card className="w-full max-w-md shadow-2xl border border-slate-700/40 bg-white/95 text-slate-900">
                            <CardHeader className="space-y-1 p-6">
                                <CardTitle className="text-2xl font-bold">Sign in</CardTitle>
                                <p className="text-sm text-slate-600">Enter your credentials to continue to the workspace.</p>
                            </CardHeader>
                            <CardContent className="p-6">
                                <form className="space-y-4" onSubmit={handleSubmit}>
                                    <div>
                                        <label className="text-sm font-medium text-slate-700">Username</label>
                                        <Input
                                            value={username}
                                            onChange={e => setUsername(e.target.value)}
                                            autoComplete="username"
                                            placeholder="you@company.com"
                                            className="mt-2"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-slate-700">Password</label>
                                        <Input
                                            type="password"
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            autoComplete="current-password"
                                            placeholder="Enter your password"
                                            className="mt-2"
                                        />
                                    </div>
                                    {error && (
                                        <div className="rounded-md bg-red-50 text-red-700 text-sm px-3 py-2">
                                            {error}
                                        </div>
                                    )}

                                    <div className="flex items-center justify-start">
                                        <label className="flex items-center gap-2 text-sm text-slate-700">
                                            <input type="checkbox" className="h-4 w-4 rounded border-slate-300" />
                                            <span>Remember me</span>
                                        </label>
                                    </div>

                                    <Button type="submit" className="w-full mt-2" disabled={isSubmitting}>
                                        {isSubmitting ? 'Signing in...' : 'Sign In'}
                                    </Button>

                                    <div className="pt-4 text-center text-xs text-slate-500">
                                        By signing in you agree to company policies.
                                    </div>
                                </form>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}
